/* eslint-disable no-param-reassign */
import get from 'lodash/get';
import each from 'lodash/each';
import every from 'lodash/every';
import extend from 'lodash/extend';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import filter from 'lodash/filter';
import first from 'lodash/first';
import reduce from 'lodash/reduce';
import uniqWith from 'lodash/uniqWith';
import { keyify } from '@Hooks/LibraryInitialization/MoreUnderscore';

import { SETTINGS, TAXOMONY_STATES, UNITS_CONVERSION_RATIO } from './TreeViewConstants';

export class TreeViewService {
  taxonomiesGetter = null;

  events = {};

  setTaxonomiesGetter = (getter) => {
    this.taxonomiesGetter = getter;
  };

  setEvents = (eventsList) => {
    extend(this.events, eventsList);
  };

  getTaxonomiesList = (taxonomyType) => this.getTaxonomyById(taxonomyType).then(this.generateTaxonomiesList).catch(Logger.reject);

  getTaxonomyById = (taxonomyType, taxonomyIds) => {
    const customParams = {
      taxonomy: taxonomyType,
    };

    if (!taxonomyIds) {
      customParams.termIds = [SETTINGS.rootTaxonomyId];
    } else {
      customParams.termIds = isArray(taxonomyIds) ? taxonomyIds : [taxonomyIds];
    }

    return this.getTaxonomies(customParams).catch(Logger.reject);
  };

  searchTaxonomyByQuery = (taxonomyType, query) => {
    const customParams = {
      taxonomy: taxonomyType,
      parentsLookup: true,
      query: query.text,
      boundOperator: query.boundOperator,
    };

    return this.getTaxonomies(customParams).then(this.generateTaxonomiesList).then(this.updateSearchResults).catch(Logger.reject);
  };

  getTaxonomies = (customParams) => {
    const params = extend({}, SETTINGS.defaultParams, customParams);

    if (isFunction(this.events?.onLoadingStart)) {
      this.events.onLoadingStart();
    }

    return this.taxonomiesGetter(params).finally(() => {
      if (isFunction(this.events?.onLoadingEnd)) {
        this.events.onLoadingEnd();
      }
    });
  };

  loadTaxonomyChildren = (taxonomyType, parentTaxonomy) =>
    this.getTaxonomyById(taxonomyType, parentTaxonomy.id)
      .then((loadedTaxonomies) => {
        const loadedTaxonomy = first(loadedTaxonomies);
        const parents = parentTaxonomy?.parents.slice();
        parents.unshift(parentTaxonomy);

        const children = this.generateTaxonomiesList(loadedTaxonomy.children, parents);
        parentTaxonomy.children = children;

        return children;
      })
      .then((childrenTaxonomies) =>
        each(childrenTaxonomies, (childTaxonomy) => {
          childTaxonomy.selected = parentTaxonomy.selected;
          childTaxonomy.state = this.getStateBySelection(childTaxonomy);
        })
      )
      .catch(Logger.reject);

  generateTaxonomiesList = (taxonomies, parents = []) => {
    const taxonomiesList = [];

    each(this.sortTaxonomies(taxonomies), (taxonomy) => {
      const isEveryChildUnselected = every(taxonomy.children, (child) => !child.selected);
      // this is fix for case when a taxonomy selected, but all children are not (can be fixed on BE as well)
      if (taxonomy.selected && isEveryChildUnselected) {
        // eslint-disable-next-line no-return-assign
        each(taxonomy.children, (child) => (child.selected = taxonomy.selected));
      }

      const listItem = this.getListItem(taxonomy, parents);
      taxonomiesList.push(listItem);

      if (get(taxonomy, 'children.length', false)) {
        const parentsForChildren = [];
        parentsForChildren.push(...parents); // we need to keep proper links on parent elements, do not copy whole array
        parentsForChildren.unshift(listItem);

        const children = this.generateTaxonomiesList(taxonomy.children, parentsForChildren);

        listItem.children = filter(children, (child) => {
          const firstParent = first(child.parents);
          return firstParent.id === listItem.id;
        });
        taxonomiesList.push(...children);
      }
    });

    return taxonomiesList;
  };

  getListItem = (taxonomy, parents = []) => {
    const isFolder = this.isTaxonomyFolder(taxonomy);
    const selected = !!taxonomy.selected;
    const state = this.getStateBySelection(taxonomy);

    return extend({}, taxonomy, {
      expanded: isFolder && taxonomy.selected,
      visible: taxonomy.selected,
      nestingLevel: parents.length,
      children: [],
      parents,
      isFolder,
      selected,
      state,
      hashKey: keyify(taxonomy, ['id', 'name']),
    });
  };

  sortTaxonomies = (taxonomies) => {
    const folders = [];
    const items = [];

    each(taxonomies, (taxonomy) => {
      const properCollection = this.isTaxonomyFolder(taxonomy) ? folders : items;
      properCollection.push(taxonomy);
    });

    return folders.concat(items);
  };

  selectTaxonomy = (taxonomy) => {
    if (taxonomy.isFolder) {
      taxonomy.selected = taxonomy.state === TAXOMONY_STATES.SELECTED;
    }

    taxonomy.selected = !taxonomy.selected;
    taxonomy.state = this.getStateBySelection(taxonomy);
    this.setTaxonomiesProperties(taxonomy.children, { selected: taxonomy.selected, state: taxonomy.state });

    each(taxonomy.parents, (parent) => {
      parent.state = this.getTaxonomyState(parent);
      parent.selected = parent.state !== TAXOMONY_STATES.UNSELECTED;
    });
  };

  getSelectedTaxonomiesData = (taxonomies) => {
    const selectedRootTaxonomies = [];
    let selectedTaxonomies = [];
    let totalHitCount = 0;

    each(taxonomies, (taxonomy) => {
      let childrenData;
      let taxonomyHitCount = 0;

      if (taxonomy.state === TAXOMONY_STATES.SELECTED) {
        if (taxonomy.nestingLevel === 0) {
          selectedRootTaxonomies.push(taxonomy);
        }

        taxonomyHitCount = taxonomy.hitCount;
        selectedTaxonomies.push(taxonomy);
      }

      if (taxonomy.state === TAXOMONY_STATES.HALFSELECTED) {
        if (taxonomy.nestingLevel === 0) {
          selectedRootTaxonomies.push(taxonomy);
        }

        childrenData = this.getSelectedTaxonomiesData(taxonomy.children);
        selectedTaxonomies = selectedTaxonomies.concat(childrenData.selectedTaxonomies);
        taxonomyHitCount = childrenData.totalHitCount;
      }
      totalHitCount += taxonomyHitCount;
    });

    return { selectedTaxonomies, totalHitCount, selectedRootTaxonomies };
  };

  // eslint-disable-next-line class-methods-use-this
  removeDuplications = (taxonomies) =>
    // eslint-disable-next-line no-shadow
    uniqWith(taxonomies, (first, last) => first.name === last.name);

  updateSearchResults = (taxonomiesList) => {
    // update is recursive, so we need pass only root taxonomy (first in our list)
    const rootTaxonomy = first(taxonomiesList);

    if (rootTaxonomy) {
      this.updateTaxonomies([rootTaxonomy]);
    }

    return taxonomiesList;
  };

  updateTaxonomies = (taxonomies) => {
    each(taxonomies, (taxonomy) => {
      if (!taxonomy.isFolder) {
        taxonomy.state = this.getStateBySelection(taxonomy);
        return;
      }

      this.updateTaxonomies(taxonomy.children);
      taxonomy.state = this.getTaxonomyState(taxonomy);
      taxonomy.selected = taxonomy.state !== TAXOMONY_STATES.UNSELECTED;
      taxonomy.expanded = taxonomy.state === TAXOMONY_STATES.HALFSELECTED;

      if (taxonomy.state === TAXOMONY_STATES.SELECTED) {
        each(taxonomy.children, (child) => {
          child.visible = false;
        });
      }
    });
  };

  // eslint-disable-next-line class-methods-use-this
  getStateBySelection = (taxonomy) => (taxonomy.selected ? TAXOMONY_STATES.SELECTED : TAXOMONY_STATES.UNSELECTED);

  // eslint-disable-next-line class-methods-use-this
  getTaxonomyState = (taxonomy) => {
    const childrenStatesSum = reduce(
      taxonomy.children,
      (tempSum, child) => {
        const state = child.state ? child.state : 0;

        return tempSum + state;
      },
      0
    );
    let state = TAXOMONY_STATES.UNSELECTED;
    const childrenLength = get(taxonomy, 'children.length', 0);
    if ((childrenLength && childrenStatesSum === childrenLength * 2) || (!childrenLength && taxonomy.selected)) {
      state = TAXOMONY_STATES.SELECTED;
    } else if (childrenStatesSum > 0 && childrenStatesSum < childrenLength * 2) {
      state = TAXOMONY_STATES.HALFSELECTED;
    }

    return state;
  };

  // eslint-disable-next-line class-methods-use-this
  isTaxonomyFolder = (taxonomy) =>
    taxonomy.isFolder || !!(get(taxonomy, 'children.length', false) || taxonomy.childrenCount || taxonomy.hasChildren);

  // eslint-disable-next-line default-param-last
  setTaxonomiesProperties = (taxonomies, properties, noRecursion = false, condition) => {
    each(taxonomies, (taxonomy) => {
      const isTaxonomyExtended = isFunction(condition) ? condition(taxonomy) : true;

      if (isTaxonomyExtended) {
        extend(taxonomy, properties);
      }

      if (taxonomy.children && !noRecursion) {
        this.setTaxonomiesProperties(taxonomy.children, properties, noRecursion, condition);
      }
    });

    return taxonomies;
  };

  isElementsWidthMoreThenMaximal = (elements, maximalWidth) => {
    const totalWidth = reduce(
      elements,
      (tmpSum, element) => {
        const elementStyle = element.currentStyle || window.getComputedStyle(element);
        const marginRight = this.parseMargin(elementStyle.marginRight);
        const marginLeft = this.parseMargin(elementStyle.marginLeft);

        return tmpSum + element.offsetWidth + marginRight + marginLeft;
      },
      0
    );
    return Math.floor(totalWidth) > maximalWidth;
  };

  // eslint-disable-next-line class-methods-use-this
  parseMargin = (margin) => {
    let result = 0;

    const [value, units] = margin.match(/[.\d]+|[a-z]+/g);

    if (UNITS_CONVERSION_RATIO[units]) {
      result = value / UNITS_CONVERSION_RATIO[units];
    }

    return result;
  };
}

const TreeView = new TreeViewService();

export default TreeView;
